import PremiumHeader from "@/components/premium/PremiumHeader";
import SEO from "@/components/SEO";
import PremiumFooter from "@/components/premium/PremiumFooter";
import StickyContactBar from "@/components/premium/StickyContactBar";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Stethoscope, Clock, Wifi, MapPin, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const TravelingNursePage = () => {
  const benefits = [
    "Close to Health First hospitals",
    "13-week and extended assignments",
    "Fully furnished—move-in ready",
    "High-speed WiFi for charting",
    "Quiet space to decompress",
    "Private parking",
    "Beach access for days off",
    "Flexible lease terms",
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SEO
        title="Travel Nurse Housing Melbourne FL | Furnished Rental Near Health First Hospitals"
        description="Furnished travel nurse housing near Health First and Melbourne FL hospitals. 13-week friendly leases, fully equipped 4BR beach house, 3-minute walk to the beach in Indialantic."
        path="/traveling-nurse-housing"
      />
      <PremiumHeader />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/yw3ssr2w_IMG_6435.png"
              alt="Travel Nurse Housing"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#0A1628]/70" />
          </div>
          <div className="relative z-10 container-premium pb-20 pt-40">
            <div className="gold-line mb-6" />
            <span className="label-sm text-[#C9A962]">Healthcare Housing</span>
            <h1 className="headline-xl text-white mt-4 mb-6 max-w-3xl">
              Travel Nurse Housing in Melbourne, FL
            </h1>
            <p className="body-lg text-white/80 max-w-2xl mb-8">
              Comfortable, fully furnished housing for healthcare professionals on assignment. 
              Close to Health First hospitals and medical centers.
            </p>
            <Link to="/contact">
              <Button className="bg-[#C9A962] text-[#0A1628] hover:bg-[#B8954D] rounded-none px-8 py-6 text-base">
                Check Assignment Availability
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Benefits */}
        <section className="section-lg">
          <div className="container-premium">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="headline-lg text-[#0A1628] mb-6">Housing That Understands Your Schedule</h2>
                <p className="body-md text-[#0A1628]/60 mb-8">
                  After long shifts, you deserve more than a cramped hotel room. Come home to a 
                  spacious private residence with everything you need to rest, recharge, and enjoy 
                  Florida on your days off.
                </p>
                <div className="space-y-3 mb-8">
                  {benefits.map((b, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-center gap-3"
                    >
                      <Check className="w-4 h-4 text-[#C9A962]" />
                      <span className="text-sm text-[#0A1628]/70">{b}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#0A1628] p-8 text-center">
                  <Stethoscope className="w-8 h-8 text-[#C9A962] mx-auto mb-4" />
                  <p className="font-display text-2xl text-white mb-1">10 min</p>
                  <p className="text-sm text-white/60">to Health First</p>
                </div>
                <div className="bg-[#F7F3ED] p-8 text-center">
                  <Clock className="w-8 h-8 text-[#0A1628] mx-auto mb-4" />
                  <p className="font-display text-2xl text-[#0A1628] mb-1">13+</p>
                  <p className="text-sm text-[#0A1628]/60">Week Stays</p>
                </div>
                <div className="bg-[#F7F3ED] p-8 text-center">
                  <Wifi className="w-8 h-8 text-[#0A1628] mx-auto mb-4" />
                  <p className="font-display text-2xl text-[#0A1628] mb-1">Fast</p>
                  <p className="text-sm text-[#0A1628]/60">WiFi for Charting</p>
                </div>
                <div className="bg-[#0A1628] p-8 text-center">
                  <MapPin className="w-8 h-8 text-[#C9A962] mx-auto mb-4" />
                  <p className="font-display text-2xl text-white mb-1">3 min</p>
                  <p className="text-sm text-white/60">to Beach</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-lg bg-[#0A1628]">
          <div className="container-premium text-center">
            <h3 className="headline-md text-white mb-4">Starting a New Assignment?</h3>
            <p className="body-md text-white/60 mb-8 max-w-xl mx-auto">
              Secure comfortable housing before your start date. We work with travel nursing agencies and direct bookings.
            </p>
            <Link to="/contact">
              <Button className="bg-[#C9A962] text-[#0A1628] hover:bg-[#B8954D] rounded-none px-8 py-4">
                Request Assignment Quote
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <PremiumFooter />
      <StickyContactBar />
    </div>
  );
};

export default TravelingNursePage;
